import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Link2,
  ExternalLink,
  Factory,
  Package,
  Trash2,
  Unlink,
  CheckCircle2,
  Clock,
  Hourglass
} from 'lucide-react';
import authService from '../services/authService';

/**
 * LinkedOrdersSection - 统一展示关联采购单和工单的组件
 *
 * 替代 SalesDetailPage 中两处分散的展示逻辑
 */
const LinkedOrdersSection = ({
  linkedPOs = [],
  linkedPRDs = [],
  onUnlinkPO,
  onUnlinkPRD,
  onDeletePRD,
  onViewInbound,
}) => {
  const navigate = useNavigate();
  const isAdmin = authService.isAdmin();

  if (linkedPOs.length === 0 && linkedPRDs.length === 0) {
    return null;
  }

  const getPOStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmed':
        return (
          <>
            <CheckCircle2 className="h-3 w-3 text-success" />
            <Badge variant="outline" className="text-xs h-5 bg-success/10 text-success">
              已确认
            </Badge>
          </>
        );
      case 'submitted':
        return (
          <>
            <Clock className="h-3 w-3 text-info" />
            <Badge variant="outline" className="text-xs h-5 bg-info/10 text-info">
              已提交
            </Badge>
          </>
        );
      default:
        return (
          <>
            <Hourglass className="h-3 w-3 text-muted-foreground" />
            <Badge variant="outline" className="text-xs h-5">
              草稿
            </Badge>
          </>
        );
    }
  };

  const getPRDStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return (
          <Badge variant="outline" className="text-xs h-5 bg-success/10 text-success">
            已完成
          </Badge>
        );
      case 'in_progress':
        return (
          <Badge variant="outline" className="text-xs h-5 bg-info/10 text-info">
            生产中
          </Badge>
        );
      case 'released':
        return (
          <Badge variant="outline" className="text-xs h-5 bg-purple-50 text-purple-600 border-purple-200">
            已下发
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="text-xs h-5 bg-muted">
            {status || '草稿'}
          </Badge>
        );
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
      {/* Linked Purchase Orders */}
      {linkedPOs.length > 0 && (
        <Card className="border bg-card border-info/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <Link2 className="h-5 w-5 text-primary" />
              <h3 className="font-semibold">关联采购订单</h3>
              <Badge variant="outline">{linkedPOs.length}</Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {linkedPOs.map((po, index) => (
                <Card
                  key={po._id || po.purchase_order_id || index}
                  className="border cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => navigate(`/purchases/${encodeURIComponent(po._id)}/view`)}
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-primary text-sm">{po.purchase_order_id}</span>
                      <div className="flex items-center gap-1">
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6"
                          onClick={(e) => {
                            e.stopPropagation();
                            onViewInbound?.(po);
                          }}
                          title="入库记录"
                        >
                          <Package className="h-3 w-3" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            onUnlinkPO?.(po);
                          }}
                          title="取消关联并删除"
                        >
                          <Unlink className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{po.supplier_name}</p>
                    <div className="flex items-center gap-1 mt-2">
                      {getPOStatusBadge(po.status)}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Linked Production Orders */}
      {linkedPRDs.length > 0 && (
        <Card className="border bg-card border-purple-500/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-4">
              <Factory className="h-5 w-5 text-purple-500" />
              <h3 className="font-semibold">关联生产订单</h3>
              <Badge variant="outline" className="bg-purple-50 text-purple-600 border-purple-200">
                {linkedPRDs.length}
              </Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {linkedPRDs.map((prd) => (
                <Card
                  key={prd._id}
                  className="border-purple-200 hover:shadow-md transition-shadow"
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between mb-1">
                      <div
                        className="flex items-center gap-1 cursor-pointer hover:underline"
                        onClick={() => navigate(`/production/${prd._id}`)}
                      >
                        <span className="font-medium text-purple-600 text-sm">{prd.production_order_id}</span>
                        <ExternalLink className="h-3 w-3 text-muted-foreground" />
                      </div>
                      <div className="flex items-center gap-1">
                        {getPRDStatusBadge(prd.status)}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-warning hover:text-warning hover:bg-warning/10"
                          onClick={(e) => {
                            e.stopPropagation();
                            onUnlinkPRD?.(prd);
                          }}
                          title="取消关联"
                        >
                          <Unlink className="h-3 w-3" />
                        </Button>
                        {isAdmin && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-6 w-6 text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (window.confirm(`确定要永久删除工单 ${prd.production_order_id} 吗？此操作不可恢复！`)) {
                                onDeletePRD?.(prd._id);
                              }
                            }}
                            title="永久删除"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      <p>计划: {prd.planned_quantity} 件</p>
                      <p>完成: {prd.completed_quantity} 件</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default LinkedOrdersSection;
